import React, { useMemo } from 'react';
import { Icon, InteractiveDiv, Loader, SimpleCard, Typography, } from '@pinwheel/origami';
import { DocumentUploadsStatus } from '../../types/document-uploads';
import { useGetLocalizedString } from '../../hooks/useGetLocalizedString';
var DocumentUploadsCard = function (_a) {
    var fileName = _a.fileName, uploadStatus = _a.uploadStatus, onRepair = _a.onRepair, onDelete = _a.onDelete, sx = _a.sx;
    var SuccessStatusIcon = useMemo(function () { return (React.createElement(Icon, { icon: "checkmarkCircleInverted", size: 16, color: {
            type: 'theme',
            colorLegacy: 'Secondary/50',
            colorNext: 'success/default',
        }, 
        // Removes the extra bottom margin when wrapped around a container div, so
        // that it's consistent with the dots loader which doesn't have any margin.
        sx: { display: 'block' } })); }, []);
    var InProgressStatusIcon = useMemo(function () { return (React.createElement(Loader, { type: "dots", size: 16, containerWidth: 16, containerHeight: 16 })); }, []);
    var FailureStatusIcon = useMemo(function () { return (React.createElement(Icon, { icon: "exclamation", size: 16, color: {
            type: 'theme',
            colorLegacy: 'Red/50',
            colorNext: 'error/default',
        }, 
        // Removes the extra bottom margin when wrapped around a container div, so
        // that it's consistent with the dots loader which doesn't have any margin.
        sx: { display: 'block' } })); }, []);
    var DeleteActionIcon = useMemo(function () { return (React.createElement(Icon, { icon: "xThin", size: 16, color: {
            type: 'theme',
            colorLegacy: 'Neutral/50',
            colorNext: 'neutral/default',
        }, "aria-label": "Delete" })); }, []);
    var RepairActionIcon = useMemo(function () { return (React.createElement(Icon, { icon: "chevronRightThin", size: 16, color: {
            type: 'theme',
            colorLegacy: 'Neutral/50',
            colorNext: 'neutral/default',
        }, "aria-label": "Repair" })); }, []);
    var statusDataMap = useMemo(function () {
        var _a;
        return (_a = {},
            _a[DocumentUploadsStatus.QUEUED] = {
                statusIcon: InProgressStatusIcon,
                localizationKey: 'UPLOADING',
            },
            _a[DocumentUploadsStatus.UPLOADING] = {
                statusIcon: InProgressStatusIcon,
                localizationKey: 'UPLOADING',
            },
            _a[DocumentUploadsStatus.UPLOADED] = {
                statusIcon: SuccessStatusIcon,
                actionIcon: DeleteActionIcon,
                onActionIconClick: onDelete,
                localizationKey: 'UPLOADED',
            },
            _a[DocumentUploadsStatus.FAILED] = {
                statusIcon: FailureStatusIcon,
                actionIcon: RepairActionIcon,
                onActionIconClick: onRepair,
                localizationKey: 'FAILED',
            },
            _a[DocumentUploadsStatus.ERROR] = {
                statusIcon: FailureStatusIcon,
                actionIcon: RepairActionIcon,
                onActionIconClick: onRepair,
                localizationKey: 'FAILED',
            },
            _a);
    }, [
        DeleteActionIcon,
        FailureStatusIcon,
        InProgressStatusIcon,
        onDelete,
        onRepair,
        RepairActionIcon,
        SuccessStatusIcon,
    ]);
    var getLocalizedString = useGetLocalizedString();
    var _b = statusDataMap[uploadStatus], statusIcon = _b.statusIcon, actionIcon = _b.actionIcon, onActionIconClick = _b.onActionIconClick, localizationKey = _b.localizationKey;
    var uploadStatusText = useMemo(function () { return getLocalizedString(localizationKey); }, [getLocalizedString, localizationKey]);
    return (React.createElement(SimpleCard, { sx: sx },
        React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                // Note: the flex `gap` property is unsupported in IE11, so set margin
                // on every child element instead. https://caniuse.com/flexbox-gap
            } },
            React.createElement("div", { style: { marginRight: '8px' } }, statusIcon),
            React.createElement("div", { style: {
                    marginRight: '8px',
                    flexGrow: 1,
                    // Fallback (IE11 / Safari <15.4): prevent text width from exceeding
                    // the container size if overflow-wrap: anywhere is unsupported
                    minWidth: 0,
                } },
                React.createElement(Typography, { variant: "body", color: "default", forceLineWrap: true, maxLines: 2 }, fileName),
                React.createElement(Typography, { variant: "caption", color: "light" }, uploadStatusText)),
            actionIcon && (React.createElement(InteractiveDiv, { style: { cursor: 'pointer' }, onClick: onActionIconClick }, actionIcon)))));
};
export default DocumentUploadsCard;
